import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './MobileGamesTerms.css';

const MobileGamesTerms = () => {
  const [activeGame, setActiveGame] = useState(null);

  // Define your mobile games here - this should match your MobileGames.js
  const mobileGames = [
    {
      id: 'game1',
      name: 'Game Title 1',
      icon: '📱',
      lastUpdated: '2024-01-11'
    },
    {
      id: 'game2',
      name: 'Game Title 2',
      icon: '🎮',
      lastUpdated: '2024-01-11'
    },
    // Add more games as needed
  ];

  const toggleGame = (gameId) => {
    setActiveGame(activeGame === gameId ? null : gameId);
  };

  return (
    <>
      <SEO
        title="Mobile Games Terms & Conditions"
        description="Read the Terms and Conditions for IdeaPetal mobile games. Understand the rules and guidelines for using our mobile applications."
        keywords="mobile games terms, app terms, mobile app conditions, game terms"
      />
      <div className="mobile-games-terms-page">
        <div className="legal-header">
          <div className="container">
            <h1>Mobile Games Terms & Conditions</h1>
            <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <div className="legal-content">
          <div className="container">
            {/* General Terms Section */}
            <section className="general-terms-section">
              <h2>General Terms & Conditions</h2>
              <p className="section-intro">
                The following terms apply to all IdeaPetal mobile games. Each game may also have specific terms 
                listed in its individual section below.
              </p>

              <div className="terms-section">
                <h3>1. Acceptance of Terms</h3>
                <p>
                  By downloading, installing, accessing, or using any mobile game application ("App") developed by 
                  IdeaPetal Games ("we", "us", "our"), you agree to be bound by these Mobile Games Terms & Conditions. 
                  If you do not agree to these terms, please do not download, install, or use our mobile games.
                </p>
              </div>

              <div className="terms-section">
                <h3>2. License to Use</h3>
                <p>
                  Subject to your compliance with these terms, IdeaPetal Games grants you a limited, non-exclusive, 
                  non-transferable, revocable license to download, install, and use our mobile games for personal, 
                  non-commercial purposes on devices that you own or control.
                </p>
                <p>This license does not allow you to:</p>
                <ul>
                  <li>Copy, modify, or create derivative works of the App</li>
                  <li>Reverse engineer, decompile, or disassemble the App</li>
                  <li>Remove, alter, or obscure any proprietary notices or labels</li>
                  <li>Rent, lease, loan, or sell access to the App</li>
                  <li>Use the App for any commercial purpose without our written consent</li>
                </ul>
              </div>

              <div className="terms-section">
                <h3>3. Account Registration</h3>
                <p>
                  Some of our mobile games may require you to create an account. When creating an account, you agree to:
                </p>
                <ul>
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your information to keep it accurate</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Accept responsibility for all activities that occur under your account</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                </ul>
              </div>

              <div className="terms-section">
                <h3>4. In-App Purchases</h3>
                <p>
                  Our mobile games may offer in-app purchases. By making an in-app purchase, you agree to pay all charges 
                  associated with the purchase. All purchases are final and non-refundable unless required by law or as 
                  otherwise stated in our refund policy.
                </p>
                <p>
                  Prices for in-app purchases may vary by region and are subject to change without notice. You are 
                  responsible for any taxes that may apply to your purchases.
                </p>
              </div>

              <div className="terms-section">
                <h3>5. Prohibited Activities</h3>
                <p>You agree not to:</p>
                <ul>
                  <li>Use cheats, exploits, automation software, bots, or any unauthorized third-party software</li>
                  <li>Interfere with or disrupt the game servers or networks</li>
                  <li>Attempt to gain unauthorized access to any part of the game</li>
                  <li>Use the game to harass, abuse, or harm other users</li>
                  <li>Impersonate any person or entity</li>
                  <li>Collect or harvest information about other users</li>
                  <li>Use the game for any illegal or unauthorized purpose</li>
                </ul>
              </div>

              <div className="terms-section">
                <h3>6. Updates and Modifications</h3>
                <p>
                  IdeaPetal Games reserves the right to update, modify, or discontinue any aspect of our mobile games 
                  at any time. We may also release updates that you must install to continue using the game. You agree 
                  to receive and install these updates.
                </p>
              </div>

              <div className="terms-section">
                <h3>7. Termination</h3>
                <p>
                  We may terminate or suspend your access to our mobile games immediately, without prior notice, for any 
                  reason, including if you breach these terms. Upon termination, your right to use the game will cease, 
                  and you must delete the App from your device.
                </p>
              </div>

              <div className="terms-section">
                <h3>8. Disclaimer of Warranties</h3>
                <p>
                  Our mobile games are provided "as is" and "as available" without warranties of any kind, either express 
                  or implied. We do not guarantee that the games will be uninterrupted, error-free, or free from viruses 
                  or other harmful components.
                </p>
              </div>

              <div className="terms-section">
                <h3>9. Limitation of Liability</h3>
                <p>
                  To the maximum extent permitted by law, IdeaPetal Games shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether 
                  incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting 
                  from your use of our mobile games.
                </p>
              </div>

              <div className="terms-section">
                <h3>10. Age Restrictions</h3>
                <p>
                  Our mobile games are intended for users who are at least 13 years old. If you are under 13, you may not 
                  use our games. If you are between 13 and 18, you represent that you have your parent's or guardian's 
                  permission to use the games and that they have agreed to these terms on your behalf.
                </p>
              </div>

              <div className="terms-section">
                <h3>11. Third-Party Services</h3>
                <p>
                  Our mobile games may integrate with third-party services, including app stores, social media platforms, 
                  and analytics services. Your use of these third-party services is subject to their respective terms and 
                  conditions and privacy policies.
                </p>
              </div>

              <div className="terms-section">
                <h3>12. Changes to Terms</h3>
                <p>
                  We reserve the right to modify these terms at any time. We will notify you of any material changes by 
                  posting the updated terms in the App or on our website. Your continued use of the App after such changes 
                  constitutes your acceptance of the new terms.
                </p>
              </div>

              <div className="terms-section">
                <h3>13. Governing Law</h3>
                <p>
                  These terms shall be governed by and construed in accordance with applicable laws. Any disputes relating 
                  to these terms will be subject to the exclusive jurisdiction of the courts in the jurisdiction where 
                  IdeaPetal Games operates.
                </p>
              </div>

              <div className="terms-section">
                <h3>14. Contact Information</h3>
                <p>
                  If you have any questions about these Mobile Games Terms & Conditions, please contact us at{' '}
                  <Link to="/contact">our contact page</Link>.
                </p>
              </div>
            </section>

            {/* Game-Specific Terms Sections */}
            <section className="game-specific-terms-section">
              <h2>Game-Specific Terms & Conditions</h2>
              <p className="section-intro">
                Each of our mobile games may have additional terms and conditions specific to that game. 
                Please review the terms for the games you play.
              </p>

              {mobileGames.length > 0 ? (
                <div className="games-terms-list">
                  {mobileGames.map((game) => (
                    <div key={game.id} className="game-terms-card">
                      <button
                        className={`game-terms-header ${activeGame === game.id ? 'active' : ''}`}
                        onClick={() => toggleGame(game.id)}
                      >
                        <div className="game-terms-title">
                          <span className="game-icon">{game.icon}</span>
                          <span className="game-name">{game.name}</span>
                        </div>
                        <span className="toggle-icon">
                          {activeGame === game.id ? '−' : '+'}
                        </span>
                      </button>
                      
                      {activeGame === game.id && (
                        <div className="game-terms-content">
                          <p className="game-terms-updated">
                            Last Updated: {new Date(game.lastUpdated).toLocaleDateString()}
                          </p>
                          
                          {/* Game-Specific Terms Content */}
                          <div className="terms-section">
                            <h4>Game-Specific Rules</h4>
                            <p>
                              The following terms are specific to {game.name}. These terms are in addition to the 
                              General Terms & Conditions above.
                            </p>
                            <p>
                              <strong>Note:</strong> Please customize the terms below for each specific game. 
                              Add game-specific rules, features, in-app purchases, age ratings, and any other 
                              relevant information.
                            </p>
                          </div>

                          <div className="terms-section">
                            <h4>Game Features</h4>
                            <ul>
                              <li>Customize this section with specific features of {game.name}</li>
                              <li>Add any unique gameplay mechanics or modes</li>
                              <li>List special features or premium content</li>
                            </ul>
                          </div>

                          <div className="terms-section">
                            <h4>In-App Purchases</h4>
                            <p>
                              {game.name} may include in-app purchases. All purchases are subject to the General Terms 
                              above. Specific pricing and items available for purchase may vary.
                            </p>
                          </div>

                          <div className="terms-section">
                            <h4>Age Rating</h4>
                            <p>
                              {game.name} is rated for users [AGE RATING]. Please ensure you meet the age requirements 
                              before downloading and playing this game.
                            </p>
                          </div>

                          <div className="terms-section">
                            <h4>Game-Specific Policies</h4>
                            <p>
                              Add any game-specific policies, rules, or guidelines here. This could include:
                            </p>
                            <ul>
                              <li>Multiplayer rules and conduct</li>
                              <li>Leaderboard policies</li>
                              <li>Account sharing restrictions</li>
                              <li>Game-specific prohibited activities</li>
                              <li>Data collection specific to this game</li>
                            </ul>
                          </div>

                          <div className="terms-section">
                            <h4>Updates and Support</h4>
                            <p>
                              {game.name} receives regular updates with new features, bug fixes, and improvements. 
                              We recommend keeping the game updated to the latest version for the best experience.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-games-message">
                  <p>No game-specific terms available at this time. Check back soon!</p>
                </div>
              )}
            </section>

            <section className="general-terms-section">
              <div className="terms-section">
                <h3>15. General Website Terms</h3>
                <p>
                  For terms and conditions related to our website and web-based games, please refer to our{' '}
                  <Link to="/terms">general Terms & Conditions</Link>.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileGamesTerms;
